import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare, Send, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import Avatar from "@/components/Avatar";
import { relativeTime } from "@/lib/utils";
import { PIPELINE_STAGES, pipelineStage, StagePicker } from "@/pages/DeveloperLeads";

function StatusPill({ status, className = "" }) {
  const s = pipelineStage(status);
  return (
    <span data-testid={`status-pill-${s.key}`} className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${s.color} ${className}`}>
      {s.label}
    </span>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => { loadConvos(); }, []);
  useEffect(() => { if (active) loadMessages(active.id); }, [active]);

  async function loadConvos() {
    try {
      const { data } = await api.get("/conversations");
      setConvos(data);
      const deepLink = params.get("c");
      if (deepLink) {
        const match = data.find((c) => c.id === deepLink);
        if (match) {
          setActive(match);
          // clear the query param without navigating away
          const next = new URLSearchParams(params);
          next.delete("c");
          setParams(next, { replace: true });
          return;
        }
      }
      if (data.length && !active) setActive(data[0]);
    } finally {
      setLoading(false);
    }
  }

  async function loadMessages(id) {
    const { data } = await api.get(`/conversations/${id}/messages`);
    setMessages(data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: 9999, behavior: "smooth" }), 50);
  }

  async function send(e) {
    e.preventDefault();
    if (!text.trim() || !active) return;
    const t = text;
    setText("");
    try {
      await api.post("/messages", { conversation_id: active.id, text: t });
      await loadMessages(active.id);
      await loadConvos();
    } catch { /* ignore */ }
  }

  async function updateStatus(next) {
    if (!active) return;
    try {
      await api.patch(`/conversations/${active.id}/status`, { status: next });
      toast.success(`Status set to ${pipelineStage(next).label}`);
      setActive({ ...active, status: next });
      setConvos((cs) => cs.map((c) => c.id === active.id ? { ...c, status: next } : c));
      await loadMessages(active.id);
    } catch {
      toast.error("Could not update status. Please try again.");
    }
  }

  const isDeveloper = user?.role === "developer";

  return (
    <div className="max-w-7xl mx-auto">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Messages</p>
      <h1 className="text-4xl font-bold text-neutral-900 mt-2">Conversations</h1>
      <p className="text-neutral-500 mt-2">Every expressed interest opens a private thread here.</p>

      {convos.length === 0 && !loading ? (
        <div className="rounded-3xl border border-dashed border-neutral-200 bg-white p-12 text-center mt-10">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4">
            <MessageSquare size={22} />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">No conversations yet</h3>
          <p className="text-sm text-neutral-500 max-w-md mx-auto mt-1">
            {user?.role === "investor"
              ? "Express interest in a development to start a private chat with the developer."
              : "Investors will reach out once they express interest in your developments."}
          </p>
          {user?.role === "investor" && (
            <Link to="/discover" data-testid="empty-discover-msgs" className="inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold text-sm mt-5">
              Discover Developments <ArrowRight size={14} />
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-220px)] min-h-[500px]">
          {/* List - hidden on mobile when a thread is active */}
          <div className={`soft-card overflow-y-auto p-2 ${active ? "hidden md:block" : ""}`}>
            {convos.map((c) => (
              <button
                key={c.id}
                data-testid={`convo-${c.id}`}
                onClick={() => setActive(c)}
                className={`w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 transition ${active?.id === c.id ? "bg-propera-light" : "hover:bg-neutral-50"}`}
              >
                <Avatar name={c.other_participant?.name} src={c.other_participant?.avatar} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-neutral-900 truncate">{c.other_participant?.name || "Unknown"}</p>
                    {c.last_message && <span className="text-[10px] text-neutral-400">{relativeTime(c.last_message.created_at)}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-neutral-500 truncate flex-1">{c.development?.name || "—"}</p>
                    <StatusPill status={c.status || "new_interest"} />
                  </div>
                  <p className="text-xs text-neutral-600 truncate mt-0.5">{c.last_message?.text || "Start a conversation..."}</p>
                </div>
                {c.unread_count > 0 && (
                  <span className="text-[10px] font-bold bg-propera text-white px-2 py-0.5 rounded-full">{c.unread_count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Thread - hidden on mobile when no thread is active */}
          <div className={`md:col-span-2 soft-card flex flex-col overflow-hidden ${active ? "" : "hidden md:flex"}`}>
            {active ? (
              <>
                <div className="px-5 py-4 border-b border-neutral-100 flex items-center gap-3">
                  <button data-testid="msg-back" onClick={() => setActive(null)} className="md:hidden -ml-2 p-2 rounded-full hover:bg-neutral-100" aria-label="Back to list">
                    <ArrowLeft size={18} />
                  </button>
                  <Link to={active.other_participant ? `/u/${active.other_participant.id}` : "#"} data-testid="thread-other-profile" className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-90">
                    <Avatar name={active.other_participant?.name} src={active.other_participant?.avatar} size={36} />
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-900 truncate">{active.other_participant?.name}</p>
                      <p className="text-xs text-neutral-500 truncate">{active.development?.name}</p>
                    </div>
                  </Link>
                  {isDeveloper ? (
                    <StagePicker current={active.status || "new_interest"} onChange={updateStatus} />
                  ) : (
                    <StatusPill status={active.status || "new_interest"} />
                  )}
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
                  {messages.map((m) => {
                    if (m.kind === "system") {
                      return (
                        <div key={m.id} className="flex justify-center">
                          <div data-testid={`system-msg-${m.id}`} className="text-[11px] uppercase tracking-wider text-neutral-500 bg-neutral-100 rounded-full px-3 py-1">
                            {m.text}
                          </div>
                        </div>
                      );
                    }
                    const isMe = m.sender_id === user?.id;
                    const isInterest = m.kind === "interest";
                    return (
                      <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${isMe ? "bg-propera text-white rounded-br-md" : "bg-neutral-100 text-neutral-800 rounded-bl-md"}`}>
                          {isInterest && (
                            <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${isMe ? "text-white/80" : "text-propera"}`}>Interest Expressed</p>
                          )}
                          {m.text}
                          <p className={`text-[10px] mt-1 ${isMe ? "text-white/70" : "text-neutral-500"}`}>{relativeTime(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="px-4 py-3 border-t border-neutral-100 flex items-center gap-2">
                  <input
                    data-testid="message-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm"
                  />
                  <button data-testid="send-message" type="submit" className="bg-propera hover:bg-propera-hover text-white p-3 rounded-full transition">
                    <Send size={16} />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-neutral-400 text-sm">Select a conversation</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
